/** One bounded, reconnecting DANDU class SSE consumer with cookie/header auth.
 *
 * Uses fetch streaming so PIN-protected Teacher Mode can send its normal
 * X-Smartboard-Teacher-Session header WITHOUT putting secrets in URLs.
 * Reconnects with exponential backoff. The caller owns safe HTTP fallback.
 */
import { useEffect, useRef, useState } from 'react';
import { apiUrl } from '../apiBase';

const MAX_BACKOFF_MS = 60000;

export async function consumeClassLive(body, onEvent, signal) {
  if (!body || typeof body.getReader !== 'function') throw new Error('SSE streaming unsupported');
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (!signal.aborted) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
      if (buffer.length > 16384) throw new Error('SSE event too large');
      let end;
      while ((end = buffer.indexOf('\n\n')) !== -1) {
        const raw = buffer.slice(0, end);
        buffer = buffer.slice(end + 2);
        const type = raw.split('\n').find((line) => line.startsWith('event: '));
        const data = raw.split('\n').filter((line) => line.startsWith('data: '));
        if (type && data.length) {
          try {
            onEvent(type.slice(7).trim(), JSON.parse(data.map((line) => line.slice(6)).join('\n')));
          } catch (_) { /* malformed optional event is ignored */ }
        }
      }
    }
  } finally {
    try { await reader.cancel(); } catch (_) { /* already cancelled */ }
  }
}

export default function useDanduClassLive({
  enabled, path, sessionHeader, onSnapshot, onUnauthorized, onResync,
}) {
  const [connected, setConnected] = useState(false);
  const callbacks = useRef({});
  callbacks.current = { onSnapshot, onUnauthorized, onResync };
  useEffect(() => {
    let active = true;
    let controller = null;
    let retry = null;
    let failures = 0;
    const announce = (value) => {
      if (!active) return;
      setConnected(value);
      window.dispatchEvent(new CustomEvent('dandu:class-live:status', { detail: { connected: value } }));
    };
    if (!enabled || !path) {
      announce(false);
      return () => { active = false; };
    }
    const connect = async () => {
      if (!active || controller) return;
      controller = new AbortController();
      const signal = controller.signal;
      let received = false;
      try {
        const response = await fetch(apiUrl(path), {
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
            ...(sessionHeader ? { 'X-Smartboard-Teacher-Session': sessionHeader } : {}),
          },
          signal,
          cache: 'no-store',
        });
        if (response.status === 401 || response.status === 403) {
          callbacks.current.onUnauthorized?.(response.status);
          return;
        }
        if (!response.ok || !response.body) throw new Error('Class SSE unavailable');
        await consumeClassLive(response.body, (event, payload) => {
          if (!active || signal.aborted) return;
          if (event === 'ready' || event === 'snapshot') {
            received = true;
            failures = 0;
            announce(true);
            if (event === 'snapshot') callbacks.current.onSnapshot?.(payload);
          } else if (event === 'book_reward') {
            window.dispatchEvent(new CustomEvent('dandu:class-live:reward', { detail: payload }));
          } else if (event === 'resync') {
            // The bounded queue lost one or more hints. Refresh canonical data
            // and reconnect so status consumers get a fresh snapshot.
            window.dispatchEvent(new CustomEvent('dandu:class-live:resync'));
            callbacks.current.onResync?.();
            controller.abort();
          }
        }, signal);
      } catch (_) { /* HTTP fallback below remains available */ }
      finally {
        if (controller?.signal === signal) controller = null;
        if (!active) return;
        announce(false);
        // Never spin on listener/DB failure. Bounded retries preserve
        // recovery without recreating 2s polling during incidents.
        failures = received ? 0 : Math.min(failures + 1, 5);
        retry = window.setTimeout(connect, Math.min(MAX_BACKOFF_MS, 3000 * 2 ** Math.max(0, failures - 1)));
      }
    };
    connect();
    return () => {
      active = false;
      if (retry !== null) window.clearTimeout(retry);
      if (controller) controller.abort();
      window.dispatchEvent(new CustomEvent('dandu:class-live:status', { detail: { connected: false } }));
    };
  }, [enabled, path, sessionHeader]);
  return connected;
}
